import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../utils/api';

const PROFILE_QUERY_KEY = ['/company-profile'];
const SECTION_DEFINITIONS_QUERY_KEY = ['/company-profile/section-definitions'];

export const getCompanyProfile = ({ accessToken, params } = {}) =>
  queryOptions({
    queryKey: [...PROFILE_QUERY_KEY, params],
    queryFn: async () => {
      const res = await axiosGet('/company-profile', { params }, accessToken);
      return res.data;
    },
  });

export const getProfileSectionDefinitions = ({ accessToken, params } = {}) =>
  queryOptions({
    queryKey: [...SECTION_DEFINITIONS_QUERY_KEY, params],
    queryFn: async () => {
      const res = await axiosGet('/company-profile/section-definitions', { params }, accessToken);
      return res.data;
    },
  });

export const useCreateCompanyProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data = {}) => axiosPost('/company-profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useUpdateCompanyProfileSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => axiosPut('/company-profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useStubUpdateCompanyProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => axiosPost('/company-profile/update', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useAddFileSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      // Let the browser/axios set multipart boundary automatically.
      return axiosPost('/company-profile/sources/file', formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useAddUrlSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => axiosPost('/company-profile/sources/url', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useDeleteProfileSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => axiosDelete(`/company-profile/sources/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useUpdateSourceSharing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, sharedWithLeadAgents }) =>
      axiosPut(`/company-profile/sources/${id}/sharing`, { sharedWithLeadAgents }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useRetrySourceProcessing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => axiosPost(`/company-profile/sources/${id}/retry`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useSaveProfileSection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ definitionId, programId, action, entries }) =>
      axiosPut(`/company-profile/sections/${definitionId}`, {
        programId,
        action,
        entries,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};

export const useResetCompanyProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => axiosDelete('/company-profile'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });
};
